import React from 'react';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconDashboardItemSvg } from 'apps/sigma2-ui/src/components/svg/DashboardItemIcon';
import { MoreItemsIconSvg } from 'apps/sigma2-ui/src/components/svg/MoreItemsIcon';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { IconEditOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconEditOutline';
import { IconDuplicateOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconDuplicateOutline';
import { Dropdown } from 'apps/sigma2-ui/src/components/Dropdown';
import { DropdownItemProps } from 'apps/sigma2-ui/src/components/Dropdown';
import { DropdownDivider } from 'apps/sigma2-ui/src/components/Dropdown';
import { IconTrashCanSvg } from 'apps/sigma2-ui/src/components/svg/IconTrashCan';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { noop } from 'lodash';
import { EditableName } from 'apps/sigma2-ui/src/components/dashboards/EditableName';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { DashboardRecordDto } from 'libs/end-to-end/src/dashboard';
import { PublicIndicatorPill } from 'apps/sigma2-ui/src/components/dashboards/PublicIndicatorPill';

export const DashboardListItem = ({
  dashboard,
  openDashboardDropdown,
  setOpenDashboardDropdown,
}: {
  dashboard: DashboardRecordDto;
  openDashboardDropdown: string | undefined;
  setOpenDashboardDropdown: Setter<string | undefined>;
}) => {
  const language = useLanguage();
  const {
    deleteDashboard,
    duplicateDashboard,
    updateDashboard,
    updateDashboardDraftName,
  } = useDashboards();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isDropdownOpen = useMemo(
    () => openDashboardDropdown === dashboard.id,
    [dashboard.id, openDashboardDropdown],
  );

  const setIsDropdownOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setOpenDashboardDropdown(dashboard.id);
      } else if (isDropdownOpen) {
        setOpenDashboardDropdown(undefined);
      }
    },
    [dashboard.id, isDropdownOpen, setOpenDashboardDropdown],
  );

  const handleDeleteDashboard = useCallback(async () => {
    setIsDeleting(true);
    await deleteDashboard({ dashboardId: dashboard.id });
    setIsDeleting(false);
  }, [dashboard.id, deleteDashboard]);

  const handleDuplicateDashboard = useCallback(async () => {
    setIsUpdating(true);
    await duplicateDashboard({ dashboardId: dashboard.id });
    setIsUpdating(false);
  }, [dashboard.id, duplicateDashboard]);

  const triggerEditDashboardName = useCallback(() => {
    setIsEditing((isEditing) => !isEditing);
  }, []);

  const onAfterSave = useCallback(
    async (name: string) => {
      if (name !== dashboard.name) {
        const updated = await updateDashboard(dashboard.id, {
          name,
          charts: dashboard.charts,
          isPublic: dashboard.isPublic,
          globalFilters: dashboard.globalFilters,
          databaseRevisionId: dashboard.databaseRevisionId,
        });

        if (updated) {
          updateDashboardDraftName(updated.name);
        }
      }
      setIsEditing(false);
    },
    [
      dashboard.charts,
      dashboard.globalFilters,
      dashboard.id,
      dashboard.isPublic,
      dashboard.name,
      dashboard.databaseRevisionId,
      updateDashboard,
      updateDashboardDraftName,
    ],
  );

  const dropdownItems = useMemo<DropdownItemProps[]>(
    () => [
      {
        key: 'Rename',
        content: (
          <div className="flex gap-2 items-center">
            <IconEditOutlineSvg wantsCurrentColor={true} />
            <div>Rename</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: triggerEditDashboardName,
        },
      },
      {
        key: 'Duplicate',
        content: (
          <div className="flex gap-2 items-center">
            {isUpdating ? (
              <Spinner size={18} textColor="text-gray-800" />
            ) : (
              <IconDuplicateOutlineSvg />
            )}
            <div>Duplicate</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: handleDuplicateDashboard,
        },
      },
      {
        key: 'divider',
        content: <DropdownDivider />,
      },
      {
        key: 'Delete',
        content: (
          <div className="flex gap-2 items-center text-red-700">
            {isDeleting ? (
              <Spinner size={17} textColor="text-red-700" />
            ) : (
              <IconTrashCanSvg svg={{ width: 17, height: 17 }} />
            )}

            <div>Delete</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: handleDeleteDashboard,
        },
      },
    ],
    [
      handleDeleteDashboard,
      handleDuplicateDashboard,
      isDeleting,
      isUpdating,
      triggerEditDashboardName,
    ],
  );
  return (
    <div className="rounded-md border-1 border-solid border-gray-200 py-4 pl-5 pr-1 bg-white/60">
      <Spacing
        direction="horizontal"
        size="justify-between"
        className="items-center pl-1"
      >
        <TouchTarget
          className="flex-grow"
          content={() => {
            return (
              <Spacing size="" direction="horizontal" className="items-center">
                {isUpdating && isEditing ? (
                  <Spinner size={19} />
                ) : (
                  <IconDashboardItemSvg />
                )}
                <EditableName
                  name={dashboard.name}
                  size="headline-5"
                  onAfterSave={onAfterSave}
                  isEditing={isEditing}
                />
                {dashboard.isPublic && (
                  <div className="mt-1">
                    <PublicIndicatorPill />
                  </div>
                )}
              </Spacing>
            );
          }}
          behavior={
            // cancel navigate to dashboard while editing name
            isEditing
              ? {
                  type: 'onClick',
                  onClick: noop,
                }
              : {
                  type: 'href',
                  href: `/dashboards/${dashboard.id}`,
                }
          }
        />

        <Spacing
          direction="horizontal"
          size="justify-between"
          className="items-center pl-1"
        >
          <S2Typography
            translatedContent={language.lastSaved({
              date: dashboard.updatedAt,
            })}
            variant="body-7"
          />
          <Dropdown
            trigger={<MoreItemsIconSvg />}
            items={dropdownItems}
            isOpen={isDropdownOpen}
            setIsOpen={setIsDropdownOpen}
          />
        </Spacing>
      </Spacing>
    </div>
  );
};
